import { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import { WallHorizontalDatum } from "building-types/datums/datumsTypesEnum";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import {
  RoomWallPositionPlacementStyle,
  type PositionWithLevel,
  type RoomWallPosition,
} from "building-types/rooms/roomPosition";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { degreeToRadian } from "common/utils/math";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { entityParamFromSpecificWallLayer } from "doc/elements/openings/openingWallLayersUtils";
import { roomEdgeOffsetFromOutlineDeprecated } from "doc/rooms/roomOutlineIntersection";
import { roomEdgeIndexFromId } from "doc/utils/roomEdgeTools";
import { polyLine2EdgeEvalOffset, polyLine2EdgeNormal } from "geom/polyLine2";
import { vec2Rotate } from "geom/vec2";
import { entityLateralOffsetFromWallDatum, getFromOrAddToWallLayerExtentsCache } from "./openings/openingWallLayers";

export function getRoomWallPositions(
  room: Room,
  roomPosition: RoomWallPosition,
  mirror: boolean,
  roomOutlines: RoomOutlines,
  wallMasses: Table<WallMassSystem>,
  roomIndex: RoomIndex,
  wallLayerExtentsCache: WallLayerExtentsCache,
): PositionWithLevel[] {
  const { outline } = room;
  let { rotation } = roomPosition;
  const {
    edgeId,
    horizontalDatum = WallHorizontalDatum.ROOM_EDGE,
    lateralOffset,
    lateralDatum = WallLateralDatumAnchorAll.INSIDE_FACE_OF_SUBSTRATE,
    lateralOffsetNormalizedToRoomWidth,
    placementStyle = RoomWallPositionPlacementStyle.VERSION_0,
  } = roomPosition;

  const edgeIndex = roomEdgeIndexFromId(room, edgeId);
  if (edgeIndex === null) {
    return [];
  }

  const wallMassSystem = wallMasses[room.level.id];
  const {
    edgeMinTable,
    edgeMaxTable,
    largestInsideOffset,
    mostInwardLayerIntoRoom: mostInwardLayerIntoRoom,
    roomEdgeLength,
  } = getFromOrAddToWallLayerExtentsCache(room, edgeIndex, wallLayerExtentsCache, roomIndex, wallMassSystem);

  const { param: resolvedParam } = entityParamFromSpecificWallLayer({
    placement: { ...roomPosition, horizontalDatum },
    largestOffset: largestInsideOffset,
    edgeLength: roomEdgeLength,
    mirror,
    mostInwardLayerIntoRoom,
    edgeMinTable,
    edgeMaxTable,
  });

  const resolvedPlacement = {
    lateralOffset,
    lateralDatum,
    lateralOffsetNormalized: lateralOffsetNormalizedToRoomWidth,
  };

  const resolvedLateralOffset =
    placementStyle === RoomWallPositionPlacementStyle.DEPRECATED
      ? roomEdgeOffsetFromOutlineDeprecated(
          room,
          edgeIndex,
          resolvedParam,
          roomOutlines.INSIDE_OF_MASS_IGNORE_VOID,
          resolvedPlacement,
        )
      : entityLateralOffsetFromWallDatum({
          resolvedParam,
          placement: resolvedPlacement,
          edgeIndex,
          room,
          roomOutlines,
          wallMassSystem,
        });

  const [x, y] = polyLine2EdgeEvalOffset(outline, edgeIndex, -resolvedLateralOffset, resolvedParam);

  if (mirror) {
    if (rotation !== undefined) {
      rotation = -rotation;
    }
  }

  const [nx, ny] = polyLine2EdgeNormal(outline, edgeIndex, true);
  const [rnx, rny] = rotation !== undefined ? vec2Rotate(nx, ny, degreeToRadian(rotation)) : [nx, ny];

  return [
    {
      location: [x, y],
      normal: [rnx, rny],
      levelId: room.level.id,
    },
  ];
}
